import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { resolveDataCopyContent } from './utils/clipboardSelection';
import NavigationUtils from './utils/NavigationUtils';

flatpickr.localize(Italian);

/**
 * Writes text to the clipboard, with a fallback for contexts where the
 * Clipboard API is not available (e.g. iframes without permission).
 *
 * Always returns a Promise: this lets callers handle, via .then()/.catch(),
 * the asynchronous failures of the Clipboard API (e.g. permission denied) that
 * a synchronous try/catch would miss.
 */
export function writeTextToClipboard(text) {
	if (navigator.clipboard?.writeText) {
		return navigator.clipboard.writeText(text);
	}
	return new Promise((resolve, reject) => {
		const textarea = document.createElement('textarea');
		textarea.value = text;
		textarea.style.position = 'fixed';
		textarea.style.opacity = '0';
		document.body.appendChild(textarea);
		textarea.select();
		try {
			if (document.execCommand('copy')) {
				resolve();
			} else {
				reject(new Error('execCommand copy failed'));
			}
		} catch (err) {
			reject(err);
		} finally {
			document.body.removeChild(textarea);
		}
	});
}

/**
 * Escapes the HTML-special characters in a string so it can be safely embedded
 * inside an HTML fragment (e.g. the clipboard text/html payload).
 */
export function escapeHtml(text) {
	return String(text ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * Writes a list of paragraphs to the clipboard preserving their division.
 *
 * Two formats are written: text/html (each paragraph wrapped in <p>) and
 * text/plain (paragraphs joined by a blank line). The paste target decides:
 * rich/paragraph-aware editors read the HTML and rebuild the paragraphs, while
 * plain-text fields read the plain text and show the blocks as separate lines.
 *
 * Falls back to writeTextToClipboard(plain) when the async Clipboard API
 * (navigator.clipboard.write / ClipboardItem) is not available.
 *
 * @param {string[]} paragraphs
 * @returns {Promise<void>}
 */
export function writeParagraphsToClipboard(paragraphs) {
	const list = (Array.isArray(paragraphs) ? paragraphs : [paragraphs])
		.map((p) => (p ?? '').toString().trim())
		.filter(Boolean);
	const plain = list.join('\n\n');

	if (navigator.clipboard?.write && typeof window.ClipboardItem !== 'undefined') {
		const html = list.map((p) => `<p>${escapeHtml(p)}</p>`).join('');
		const item = new window.ClipboardItem({
			'text/html': new Blob([html], { type: 'text/html' }),
			'text/plain': new Blob([plain], { type: 'text/plain' }),
		});
		return navigator.clipboard.write([item]).catch(() => writeTextToClipboard(plain));
	}

	return writeTextToClipboard(plain);
}

export function copyOnClipboard(selector, message = 'Contenuto copiato') {
	const copyText = selector instanceof Element ? selector : document.getElementById(selector);
	if (!copyText) {
		return;
	}

	// Prefer the live selection inside the related text panel (agency offcanvas /
	// editor); otherwise fall back to the full data-copy attribute.
	const content = resolveDataCopyContent(copyText);
	if (!content) {
		return;
	}

	const paragraphs = content
		.split(/\n{2,}/)
		.map((p) => p.trim())
		.filter(Boolean);
	const writePromise =
		paragraphs.length > 1 ? writeParagraphsToClipboard(paragraphs) : writeTextToClipboard(content);

	Promise.resolve(writePromise)
		.then(() => {
			// Add the content to the clipboard history (if initialized).
			window.ClipboardHistory?.add(content);
			if (window.toastr && message) {
				window.toastr.success(message);
			}
		})
		.catch(() => {
			if (window.toastr) {
				window.toastr.warning('Copia non disponibile in questo contesto');
			}
		});
}

export function emptyClipboard() {
	try {
		if (navigator.clipboard?.writeText) {
			navigator.clipboard.writeText('');
		}
	} catch (_err) {
		// ignore
	}
}

export function getLastVersion() {
	const lastVersion = localStorage.getItem('naxos-version');
	if (!lastVersion) {
		setLastVersion();
	}
	return localStorage.getItem('naxos-version');
}

export function setLastVersion(version = null) {
	localStorage.setItem('naxos-version', version ?? window.version);
}

export function checkForUpdates() {
	if (getLastVersion() !== window.version) {
		$('#last-updates-button').show();
	} else {
		$('#last-updates-button').hide();
	}
}

export function openLastUpdatesWindow(url) {
	const lastVersion = getLastVersion();
	NavigationUtils.createAndOpenPanel(url + '?current_version=' + window.version + (lastVersion ? '&version=' + lastVersion : ''));
}
